import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdHotelOwner } from '../../../contexts/HotelOwnerContext';
import { useNotification } from '../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import {
    getHotelCompetitors,
    removeHotelCompetitor,
    saveHotelCompetitors,
} from '../services/competitorsStorage';
import { Competitor, StoredCompetitor } from '../types/competitors';

/**
 * Manages browser-persisted competitors for the active owner and own hotel.
 * @param hotelId Stable own hotel identifier, or null while it is unavailable.
 * @returns Scoped records, persistence status/errors and asynchronous mutations.
 */
export const useCompetitors = (hotelId: string | null) => {
    const ownerId = useIdHotelOwner();
    const { setErrorMessage } = useNotification();
    const notifyRef = useRef(setErrorMessage);
    notifyRef.current = setErrorMessage;
    const scopeKey = JSON.stringify([ownerId, hotelId]);
    const activeScopeRef = useRef(scopeKey);
    activeScopeRef.current = scopeKey;
    const mountedRef = useRef(false);
    const revisionRef = useRef(0);
    const [state, setState] = useState<{
        scopeKey: string;
        competitors: StoredCompetitor[];
        isLoading: boolean;
        error: Error | null;
    }>({
        scopeKey,
        competitors: [],
        isLoading: Boolean(ownerId && hotelId),
        error: null,
    });

    const reportError = useCallback(
        (reason: unknown, key: string, revision?: number, notify = true) => {
            const error =
                reason instanceof Error ? reason : new Error(String(reason));
            console.error('Competitor persistence failed:', error);
            if (
                mountedRef.current &&
                activeScopeRef.current === key &&
                (revision === undefined || revisionRef.current === revision)
            ) {
                setState(previous => ({
                    ...previous,
                    scopeKey: key,
                    isLoading: false,
                    error,
                }));
                if (notify) {
                    notifyRef.current(FeedbackMessages.COMMON_ERROR);
                }
            }
            return error;
        },
        []
    );

    const reload = useCallback(
        async (notify = true) => {
            revisionRef.current += 1;
            const revision = revisionRef.current;
            if (
                !mountedRef.current ||
                activeScopeRef.current !== scopeKey ||
                !ownerId ||
                !hotelId
            ) {
                throw reportError(
                    new Error(
                        'Owner and hotel are required for competitor storage.'
                    ),
                    scopeKey,
                    undefined,
                    notify
                );
            }
            setState(previous => ({
                scopeKey,
                competitors:
                    previous.scopeKey === scopeKey ? previous.competitors : [],
                isLoading: true,
                error: null,
            }));
            try {
                const competitors = await getHotelCompetitors({
                    ownerId,
                    hotelId,
                });
                if (
                    mountedRef.current &&
                    activeScopeRef.current === scopeKey &&
                    revisionRef.current === revision
                ) {
                    setState({
                        scopeKey,
                        competitors,
                        isLoading: false,
                        error: null,
                    });
                }
            } catch (reason) {
                throw reportError(reason, scopeKey, revision, notify);
            }
        },
        [ownerId, hotelId, scopeKey, reportError]
    );

    useEffect(() => {
        mountedRef.current = true;
        if (ownerId && hotelId) {
            // reload reports errors; the effect has no caller to receive a rejection.
            void reload().catch(() => undefined);
        }
        return () => {
            mountedRef.current = false;
            revisionRef.current += 1;
        };
    }, [ownerId, hotelId, reload]);

    const persist = useCallback(
        async <T>(
            operation: (scope: {
                ownerId: string;
                hotelId: string;
            }) => Promise<T>,
            notify = true
        ) => {
            if (
                !mountedRef.current ||
                !ownerId ||
                !hotelId ||
                activeScopeRef.current !== scopeKey
            ) {
                throw reportError(
                    new Error('The competitor scope is no longer active.'),
                    scopeKey,
                    undefined,
                    notify
                );
            }
            let result: T;
            try {
                result = await operation({ ownerId, hotelId });
            } catch (reason) {
                throw reportError(reason, scopeKey, undefined, notify);
            }
            if (mountedRef.current && activeScopeRef.current === scopeKey) {
                await reload(notify);
            }
            return result;
        },
        [ownerId, hotelId, scopeKey, reload, reportError]
    );

    const saveCompetitors = useCallback(
        (competitors: Competitor[]) =>
            persist(scope => saveHotelCompetitors(scope, competitors)),
        [persist]
    );
    const removeCompetitor = useCallback(
        (competitorId: string) =>
            persist(scope => removeHotelCompetitor(scope, competitorId), false),
        [persist]
    );

    const isCurrentScope = state.scopeKey === scopeKey;
    return {
        competitors:
            isCurrentScope && ownerId && hotelId ? state.competitors : [],
        isLoading:
            Boolean(ownerId && hotelId) && (!isCurrentScope || state.isLoading),
        error: isCurrentScope ? state.error : null,
        reload,
        saveCompetitors,
        removeCompetitor,
    };
};
